import { pb } from "@/lib/pocketbase";
import { ArticleStatus } from "@/components/articles/status/ProgressBadge";

export interface ArticleProgressData {
  stage: string;
  percentage: number;
  message: string;
  lastUpdated: string;
  isCompleted: boolean;
  hasError: boolean;
  estimatedTimeRemaining?: number;
}

export interface ArticleWithProgress {
  id: string;
  title: string;
  status: ArticleStatus;
  created: string;
  updated?: string;
  progress?: ArticleProgressData;
  scheduleDate?: string;
  topic?: string;
  isScheduled?: boolean;
}

class ArticleStatusService {
  private statusListeners = new Set<
    (articles: ArticleWithProgress[]) => void
  >();

  // Subscribe to status updates
  subscribe(callback: (articles: ArticleWithProgress[]) => void) {
    this.statusListeners.add(callback);
    return () => {
      this.statusListeners.delete(callback);
    };
  }

  // Retry failed article generation
  async retryGeneration(
    brandId: string,
    topic: string,
    keyword?: string,
  ): Promise<string | null> {
    try {
      const response = await fetch(`${pb.baseURL}/api/v1/posts/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: pb.authStore.token,
        },
        body: JSON.stringify({
          brandId,
          topic,
          keyword,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to retry generation");
      }

      const data = await response.json();
      return data.data.postId || null;
    } catch (error) {
      console.error("Error retrying generation:", error);
      return null;
    }
  }

  // Generate scheduled article immediately
  async generateScheduledArticle(scheduleId: string): Promise<string | null> {
    try {
      const response = await fetch(
        `${pb.baseURL}/api/v1/posts/generate-scheduled/${scheduleId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: pb.authStore.token,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to generate scheduled article");
      }

      const data = await response.json();
      return data.data.postId || null;
    } catch (error) {
      console.error("Error generating scheduled article:", error);
      return null;
    }
  }

  // Fetch all articles with their current status
  async fetchArticlesWithStatus(
    brandId: string,
  ): Promise<ArticleWithProgress[]> {
    try {
      // Fetch completed posts
      const postsResponse = await pb.collection("posts").getList(1, 50, {
        filter: `brand = "${brandId}"`,
        sort: "-created",
      });

      // Fetch scheduled articles
      const schedulesResponse = await pb
        .collection("content_schedules")
        .getList(1, 50, {
          filter: `brand = "${brandId}" && status = "scheduled"`,
          sort: "scheduleDate",
          expand: "post",
        });

      const articles: ArticleWithProgress[] = [];

      // Process completed posts
      postsResponse.items.forEach((post) => {
        articles.push({
          id: post.id,
          title: post.title || "Untitled Article",
          status: this.mapPostStatus(post.status),
          created: post.created,
          updated: post.updated,
        });
      });

      // Process scheduled articles
      schedulesResponse.items.forEach((schedule) => {
        const status = this.mapScheduleStatus(schedule.status);
        articles.push({
          id: schedule.id,
          title:
            schedule.expand?.post?.title ||
            schedule.topic ||
            "Untitled Article",
          status,
          created: schedule.created,
          scheduleDate: schedule.scheduleDate,
          topic: schedule.topic,
          isScheduled: true,
        });

        // Note: Real-time updates for in-progress articles are handled by
        // PocketBase subscriptions in the useArticleProgress hook
      });

      return articles.sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
      );
    } catch (error) {
      console.error("Error fetching articles:", error);
      return [];
    }
  }

  // Map post status to our ArticleStatus type
  private mapPostStatus(status: string): ArticleStatus {
    switch (status) {
      case "draft":
        return "draft";
      case "published":
        return "published";
      case "scheduled":
        return "scheduled";
      case "failed":
        return "failed";
      case "generating":
        return "in_progress";
      default:
        return "draft";
    }
  }

  // Map schedule status to our ArticleStatus type
  private mapScheduleStatus(status: string): ArticleStatus {
    switch (status) {
      case "scheduled":
        return "scheduled";
      case "in_progress":
        return "in_progress";
      case "completed":
        return "completed";
      case "failed":
        return "failed";
      default:
        return "scheduled";
    }
  }

  // Filter articles by status
  filterArticles(
    articles: ArticleWithProgress[],
    statusFilter: string,
  ): ArticleWithProgress[] {
    if (statusFilter === "all") {
      return articles;
    }
    return articles.filter((article) => article.status === statusFilter);
  }

  // Cleanup listeners
  cleanup() {
    this.statusListeners.clear();
  }
}

// Export singleton instance
export const articleStatusService = new ArticleStatusService();
export default articleStatusService;
